import axios from 'axios';
import { API_URL } from '../constants/api';
import {
  FieldScheduleEntry,
  CreateScheduleEntryRequest,
  UpdateScheduleEntryRequest
} from '../types/schedule';

// Public API client (no auth required)
const publicApiClient = axios.create({
  baseURL: API_URL,
});

// Field owner API client
const fieldApiClient = axios.create({
  baseURL: API_URL,
});

fieldApiClient.interceptors.request.use((config) => {
  const fieldToken = localStorage.getItem('fieldToken');
  if (fieldToken) {
    config.headers.Authorization = `Bearer ${fieldToken}`;
  }
  return config;
});

// Main referee API client
const refereeApiClient = axios.create({
  baseURL: API_URL,
});

refereeApiClient.interceptors.request.use((config) => {
  const refereeToken = localStorage.getItem('refereeToken');
  if (refereeToken) {
    config.headers.Authorization = `Bearer ${refereeToken}`;
  }
  return config;
});

// ============ PUBLIC ENDPOINTS ============

/**
 * Get field schedule for a week (public)
 */
export const getFieldWeekSchedule = async (
  fieldId: string,
  weekStart: Date
): Promise<FieldScheduleEntry[]> => {
  const response = await publicApiClient.get(
    `/api/FieldSchedule/field/${fieldId}/week`,
    { params: { weekStart: weekStart.toISOString() } }
  );
  return response.data;
};

/**
 * Get referee's shifts for a week (public)
 */
export const getRefereeWeekSchedule = async (
  refereeId: string,
  weekStart: Date
): Promise<FieldScheduleEntry[]> => {
  const response = await publicApiClient.get(
    `/api/FieldSchedule/referee/${refereeId}/week`,
    { params: { weekStart: weekStart.toISOString() } }
  );
  return response.data;
};

// ============ FIELD OWNER ENDPOINTS ============

/**
 * Create schedule entry (field owner only)
 */
export const createFieldScheduleEntry = async (
  fieldId: string,
  request: CreateScheduleEntryRequest
): Promise<FieldScheduleEntry> => {
  const response = await fieldApiClient.post(
    `/api/FieldSchedule/field/${fieldId}`,
    request
  );
  return response.data;
};

/**
 * Update schedule entry (field owner only)
 */
export const updateFieldScheduleEntry = async (
  entryId: string,
  fieldId: string,
  request: UpdateScheduleEntryRequest
): Promise<FieldScheduleEntry> => {
  const response = await fieldApiClient.put(
    `/api/FieldSchedule/${entryId}/field/${fieldId}`,
    request
  );
  return response.data;
};

/**
 * Delete schedule entry (field owner only)
 */
export const deleteFieldScheduleEntry = async (
  entryId: string,
  fieldId: string
): Promise<void> => {
  await fieldApiClient.delete(`/api/FieldSchedule/${entryId}/field/${fieldId}`);
};

// ============ MAIN REFEREE ENDPOINTS ============

/**
 * Get field schedule for a week (main referee)
 */
export const getMainRefereeWeekSchedule = async (
  weekStart: Date
): Promise<FieldScheduleEntry[]> => {
  const response = await refereeApiClient.get(
    '/api/main-referee/schedule/week',
    { params: { weekStart: weekStart.toISOString() } }
  );
  return response.data;
};

/**
 * Create schedule entry (main referee)
 */
export const createMainRefereeScheduleEntry = async (
  request: CreateScheduleEntryRequest
): Promise<FieldScheduleEntry> => {
  const response = await refereeApiClient.post(
    '/api/main-referee/schedule',
    request
  );
  return response.data;
};

/**
 * Update schedule entry (main referee)
 */
export const updateMainRefereeScheduleEntry = async (
  entryId: string,
  request: UpdateScheduleEntryRequest
): Promise<FieldScheduleEntry> => {
  const response = await refereeApiClient.put(
    `/api/main-referee/schedule/${entryId}`,
    request
  );
  return response.data;
};

/**
 * Delete schedule entry (main referee)
 */
export const deleteMainRefereeScheduleEntry = async (
  entryId: string
): Promise<void> => {
  await refereeApiClient.delete(`/api/main-referee/schedule/${entryId}`);
};

// ============ UPCOMING EVENTS ENDPOINTS ============

/**
 * Get upcoming events for a field (public) - events only, sorted by date
 */
export const getUpcomingEvents = async (
  fieldId: string,
  limit: number = 3
): Promise<FieldScheduleEntry[]> => {
  const response = await publicApiClient.get(
    `/api/FieldSchedule/field/${fieldId}/upcoming-events`,
    { params: { limit } }
  );
  return response.data;
};

/**
 * Get all future events for a field (for event selector in news creation)
 */
export const getFutureEvents = async (
  fieldId: string
): Promise<FieldScheduleEntry[]> => {
  const response = await fieldApiClient.get(
    `/api/FieldSchedule/field/${fieldId}/future-events`
  );
  return response.data;
};
